---
title: "Running reports"
slug: "running-reports"
category: "operations"
description: "Late-book lists, popularity rankings, reading activity by year group — and how to export them."
tags: ["reports", "excel", "late", "popular", "reading activity", "statistics"]
order: 2
---

Reports are the bit of library management software that most teachers never quite get around to using. BookBurrow tries to fix that by making every report one click away, printable as-is, and exportable to Excel if you want to slice the numbers yourself.

## The report hub

Click **Reports** in the sidebar. Every report is listed with a one-line description and an example of what it looks like, so you don't have to generate one to know whether it's what you want.

## The built-in reports

### Late books

The most-used report. Lists every book currently overdue, grouped by year group and class, with the pupil's name, the book's title, and the number of days late. Handy for a Monday morning nudge.

**Print-and-post tip**: this report has a **Per-class** print mode that generates one printable page per class, so form teachers can hand out reminders without sifting through the whole school.

### Most popular titles

The top twenty most-borrowed books, for any period (last month, last term, year-to-date, all time). Great for displays, book recommendations, and arguing with the head teacher about why the library needs more Jacqueline Wilson.

### Reading activity by year group

Total loans per year group, per month. Good for spotting whether Year 4 is reading less than last year, or whether a new display is driving a bump in Year 2 loans.

### Stock summary

Total books in the library, broken down by category, reading scheme, and availability. The baseline for any "how big is our library?" conversation.

### Recently added

Books catalogued in the last 30 days. Useful for a "new in the library" display.

### Never-lent

Books in your catalogue that have never been borrowed since they were added. Sometimes these are great books that just need promoting; sometimes they're the reading scheme set from 2004 that everyone's forgotten exists.

### Pupils with no loans

Pupils who haven't borrowed a single book since the start of the current academic year. A quiet way to spot reluctant readers you might want to encourage.

### Loss and damage

Books marked lost, damaged, or written off in the current year. Feeds directly into replacement-budget conversations. See [Handling lost or damaged books](/help/guides/handling-lost-or-damaged-books) for how the underlying records are produced in the first place.

## Exporting

Every report has two export buttons:

- **Print** — lays the report out for A4 paper, tidy margins, school name in the header.
- **Export Excel** — downloads a `.xlsx` file with the underlying data. Open in Excel, LibreOffice, or Google Sheets and play with it.

<Callout variant="tip" title="Copying into a presentation">
If you're making slides for a governors' meeting, the Excel export is usually easier to reshape than the printed version. Copy the cells you want and paste them into PowerPoint as an image for a clean look.
</Callout>

## Filtering and date ranges

Every report has a **date range** picker at the top. The defaults are sensible ("this academic year" for most, "all time" for stock summaries), but you can change to any custom range.

Most reports also support **filters**: year group, class, reading scheme, category. Filters combine, so you can run "most popular Year 4 fiction books, last term" in one click.

## Who can run reports?

By default, anyone in **Teacher** mode or higher can run every report in this list. Helpers in the restricted role cannot — reports can reveal pupil reading habits, which you may not want parent volunteers to see. See [PIN access and roles](/help/guides/pin-access-and-roles) for how to set this up.

## Custom reports

There's no custom-report builder inside BookBurrow — the built-ins cover what most schools actually need. But because every report exports to Excel, custom analysis is usually faster in a spreadsheet than by building a query. If you find yourself wanting a specific report that BookBurrow doesn't have, email support@bookburrow.uk — if a lot of schools want the same thing, we add it.
